import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';

import { Box, styled, Typography } from '@mui/material';

import { LanguageBar } from 'AppLayout/LanguageBar';
import { confirmEmailAndActivateAccount, LoginState, useSelectLoginState } from 'Auth/data';
import { Link, Logo } from 'Components';
import { StyledLogo } from 'Components/Styled';
import { selectGoogleSettings, selectShouldShowExternalLoginProviders } from 'Config/configSlice';
import { urls } from 'Router';
import { useAppDispatch, useAppSelector } from 'Store';

import { BottomContainer } from './BottomContainer';
import { ExternalLoginProviders } from './ExternalLoginProviders';
import { LinksContainer } from './LinksContainer';
import { LoginFormContainer } from './LoginFormContainer';

const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OrTextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const LoginPage = () => {
  const location = useLocation();
  const { code } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const loginState = useSelectLoginState();
  const googleSettings = useAppSelector(selectGoogleSettings);
  const shouldShowExternalLoginProviders = useAppSelector(selectShouldShowExternalLoginProviders);

  const [emailFromForm, setEmailFromForm] = useState<string>();
  const [confirmEmailAndActivateAccountSuccess, setConfirmEmailAndActivateAccountSuccess] =
    useState<boolean>();

  const handleEmailChange = (email: string) => setEmailFromForm(email);

  useEffect(() => {
    if (loginState === LoginState.LOGGED) {
      navigate(urls.homePage);
    }
  }, [loginState, navigate]);

  useEffect(() => {
    if (code) {
      dispatch(confirmEmailAndActivateAccount(code)).then(() =>
        setConfirmEmailAndActivateAccountSuccess(true)
      );
    }
  }, [code, dispatch]);

  const renderExternalLoginSection = () => {
    if (!shouldShowExternalLoginProviders) {
      return null;
    }

    return (
      <>
        <ExternalLoginProviders shouldShowGoogle={!!googleSettings?.clientId} />
        <OrTextContainer>{t('auth.or')}</OrTextContainer>
      </>
    );
  };

  return (
    <>
      <StyledLogo>
        <Typography noWrap>{t('auth.loginIn')}</Typography>
        <Logo variant="login" />
      </StyledLogo>

      {renderExternalLoginSection()}

      {location.state?.isAfterResetPassword ? (
        <Content>
          <Typography fontWeight="bold">{t('auth.passwordHasBeenChanged')}</Typography>{' '}
          <Typography>{t('auth.logInUsingNewPassword')}</Typography>
        </Content>
      ) : (
        ''
      )}

      {confirmEmailAndActivateAccountSuccess ? (
        <Content>
          <Typography fontWeight="bold">
            {t('auth.emailHasBeenConfirmedAndAccountHasBeenActivated')}
          </Typography>{' '}
          <Typography>{t('auth.youCanNowLogIn')}</Typography>
        </Content>
      ) : (
        ''
      )}

      <LoginFormContainer joinCode={location.state?.joinCode} onEmailChange={handleEmailChange} />

      <BottomContainer>
        <LinksContainer>
          {!location.state?.isAfterResetPassword && !confirmEmailAndActivateAccountSuccess ? (
            <>
              <Link
                to={urls.registrationPage}
                label={t('auth.createAccount')}
                shouldOpenInNewTab={false}
                state={{ joinCode: location.state?.joinCode }}
              />
              <Link
                to={urls.forgotPasswordPage}
                label={t('auth.remindPassword')}
                shouldOpenInNewTab={false}
                state={{ email: emailFromForm }}
              />
            </>
          ) : (
            ''
          )}

          <Link to={urls.treneoSite} label={t('auth.whatIsTreneo')} />
        </LinksContainer>

        <LanguageBar />
      </BottomContainer>
    </>
  );
};
